import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Paper, Alert, CircularProgress, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TableSortLabel,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';

import { reportingUsageAPI } from '../services/reportingUsageApi';
import { saveBlob, downloadError } from '../utils/download';
import { PeriodPicker, Stat, StatGrid, Legend, BarChart, CustomerPicker, useCustomers, PALETTE, num, errText, monthShort, monthLong } from '../components/ReportingUsageParts';

// Samples and tests of one customer, month by month and database by database. Props: custCode (optional) opens a customer straight away.
export default function ReportingUsageDetail({ custCode = '' }) {
  const [activeOnly, setActiveOnly] = useState(true);
  const { customers, error: listError } = useCustomers(activeOnly);
  const [selected, setSelected] = useState(custCode);
  const [dimension, setDimension] = useState('last_year');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [sort, setSort] = useState({ by: 'log_month', dir: 'desc' });

  useEffect(() => { setSelected(custCode || ''); }, [custCode]);

  useEffect(() => {
    if (!selected) { setData(null); return undefined; }
    let live = true;
    setLoading(true); setError('');
    reportingUsageAPI.customerUsage(selected, dimension)
      .then((res) => live && setData(res.data))
      .catch((err) => { if (live) { setData(null); setError(errText(err, 'Could not load usage')); } })
      .finally(() => live && setLoading(false));
    return () => { live = false; };
  }, [selected, dimension]);

  const rows = data?.rows || [];
  const databases = useMemo(() => [...new Set(rows.map((r) => r.database_name))], [rows]);
  const keys = databases.map((db, i) => ({ key: db, label: db, color: PALETTE[i % PALETTE.length] }));
  const chart = (field) => {
    const months = {};
    rows.forEach((r) => {
      const m = months[r.log_month] || (months[r.log_month] = { label: monthShort(r.log_month), values: {} });
      m.values[r.database_name] = (m.values[r.database_name] || 0) + Number(r[field] || 0);
    });
    return Object.keys(months).sort().map((k) => months[k]);
  };
  const sorted = useMemo(() => {
    const dir = sort.dir === 'asc' ? 1 : -1;
    const val = (r) => (['order_count', 'ord_task_count'].includes(sort.by) ? Number(r[sort.by] || 0) : String(r[sort.by] ?? ''));
    return [...rows].sort((a, b) => (val(a) < val(b) ? -1 : val(a) > val(b) ? 1 : 0) * dir);
  }, [rows, sort]);
  const sortBy = (by) => setSort((s) => ({ by, dir: s.by === by && s.dir === 'desc' ? 'asc' : 'desc' }));
  const head = (by, label, align) => (
    <TableCell align={align} sortDirection={sort.by === by ? sort.dir : false}>
      <TableSortLabel active={sort.by === by} direction={sort.by === by ? sort.dir : 'asc'} onClick={() => sortBy(by)}>{label}</TableSortLabel>
    </TableCell>
  );

  const exportCsv = async () => {
    try {
      const res = await reportingUsageAPI.exportCustomerCsv(selected);
      saveBlob(new Blob([res.data], { type: 'text/csv' }), `${selected.toUpperCase()}_sample_test_counts.csv`);
    } catch (err) { setError(await downloadError(err, 'CSV export failed')); }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>Usage Detail</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Samples and tests for one customer, month by month and by STARLIMS database. Periods are whole months and end before the current month.
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
        <CustomerPicker customers={customers} value={selected} onChange={setSelected} activeOnly={activeOnly} onActiveOnly={setActiveOnly} />
        <PeriodPicker value={dimension} onChange={setDimension} />
        {data && <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={exportCsv}>Export sample and test counts</Button>}
      </Box>
      {(error || listError) && <Alert severity="error" sx={{ mb: 2 }}>{error || listError}</Alert>}
      {!selected && !error && <Typography color="text.secondary">Select a customer to see their usage.</Typography>}
      {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>}
      {data && !loading && (
        <>
          <StatGrid>
            <Stat label="Total samples" value={num(data.total_orders)} sub={data.customer?.cust_name} />
            <Stat label="Total tests" value={num(data.total_tasks)} sub={data.customer?.cust_name} />
            <Stat label="Months of data" value={num(data.month_count)} sub={data.date_from ? `${data.date_from} to ${data.date_to}` : 'all time'} />
            <Stat label="Databases" value={num(databases.length)} sub={databases.slice(0, 3).join(', ')} />
          </StatGrid>
          {rows.length === 0 ? (
            <Alert severity="info">No samples or tests were reported for this customer in this period.</Alert>
          ) : (
            <>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2, mb: 2 }}>
                {[['order_count', 'Samples by database'], ['ord_task_count', 'Tests by database']].map(([field, title]) => (
                  <Paper key={field} sx={{ p: 2, minWidth: 0 }}>
                    <Typography variant="subtitle1" gutterBottom>{title}</Typography>
                    <Legend items={keys} />
                    <BarChart data={chart(field)} keys={keys} stacked ariaLabel={`${title} per month`} />
                  </Paper>
                ))}
              </Box>
              <Paper>
                <Typography variant="subtitle1" sx={{ p: 2, pb: 0 }}>Monthly data</Typography>
                <TableContainer sx={{ maxHeight: 480 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>{head('log_month', 'Month')}{head('database_name', 'Database')}{head('order_count', 'Samples', 'right')}{head('ord_task_count', 'Tests', 'right')}</TableRow>
                    </TableHead>
                    <TableBody>
                      {sorted.map((r) => (
                        <TableRow key={`${r.log_month}-${r.database_name}`} hover>
                          <TableCell>{monthLong(r.log_month)}</TableCell>
                          <TableCell>
                            <Box component="span" sx={{ display: 'inline-block', width: 9, height: 9, borderRadius: '50%', mr: 0.75, bgcolor: PALETTE[databases.indexOf(r.database_name) % PALETTE.length] }} />
                            {r.database_name}
                          </TableCell>
                          <TableCell align="right">{num(r.order_count)}</TableCell>
                          <TableCell align="right">{num(r.ord_task_count)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </>
          )}
        </>
      )}
    </Box>
  );
}
